import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight,
  Globe,
  Shield,
  Clock,
  Users,
  Award,
  Target,
  Heart,
  CheckCircle,
  Building,
  Briefcase,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Page Header
const PageHeader = () => {
  return (
    <section className="relative pt-32 pb-20 bg-[#0B2545]" data-testid="about-header">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_1px,transparent_1px),linear-gradient(to_bottom,#ffffff12_1px,transparent_1px)] bg-[size:40px_40px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4"
          >
            Who We Are
          </motion.p>
          <motion.h1 
            variants={fadeInUp}
            className="heading-font text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight leading-none mb-6"
          >
            About Sky Cargo
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="text-lg text-slate-300 max-w-2xl"
          >
            A trusted name in global logistics, delivering excellence from our headquarters 
            in Amsterdam to destinations worldwide.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// Company Story Section
const CompanyStorySection = () => {
  return (
    <section className="py-24 bg-white" data-testid="company-story-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp}>
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4">Our Story</p>
            <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight mb-6">
              Building Bridges Across Continents
            </h2>
            
            <p className="text-slate-600 leading-relaxed mb-6">
              Sky Cargo Delivery was founded with a clear vision: to revolutionize the logistics industry 
              by combining cutting-edge technology with personalized service. From our strategic location 
              in Amsterdam, one of Europe's most important logistics hubs, we've grown to serve clients 
              across more than 50 countries.
            </p>
            
            <p className="text-slate-600 leading-relaxed mb-6">
              Our journey began with a small team of dedicated professionals who believed that shipping 
              cargo should be simple, transparent, and reliable. Today, we've expanded into a full-service 
              logistics provider offering sea, air, and land freight solutions, warehousing, and 
              specialized courier services.
            </p>

            <p className="text-slate-600 leading-relaxed mb-8">
              What sets us apart is our unwavering commitment to our clients. We treat every shipment 
              as if it were our own, ensuring the highest standards of care, security, and timeliness. 
              Our success is measured not just in the volume of cargo we move, but in the lasting 
              relationships we build with our partners and customers.
            </p>

            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-6 bg-slate-50">
                <p className="heading-font text-4xl font-bold text-[#F59E0B]">50+</p>
                <p className="text-sm text-slate-600 mt-2">Countries Served</p>
              </div>
              <div className="text-center p-6 bg-slate-50">
                <p className="heading-font text-4xl font-bold text-[#F59E0B]">15K+</p>
                <p className="text-sm text-slate-600 mt-2">Annual Shipments</p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1770838517148-caacddedac1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxjYXJnbyUyMHNoaXAlMjBjb250YWluZXIlMjBzZWElMjBmcmVpZ2h0fGVufDB8fHx8MTc3MzUzODM5MHww&ixlib=rb-4.1.0&q=85"
                alt="Global Shipping Operations"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-8 -right-8 bg-[#0B2545] p-8 hidden lg:block">
                <p className="heading-font text-5xl font-bold text-[#F59E0B]">98%</p>
                <p className="text-white text-sm mt-2">On-Time Delivery Rate</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Mission & Vision Section
const MissionVisionSection = () => {
  return (
    <section className="py-24 bg-slate-50" data-testid="mission-vision-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4">What Drives Us</p>
            <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight">
              Mission & Vision
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div 
              variants={fadeInUp}
              className="bg-white p-10 border border-slate-200"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#0B2545] flex items-center justify-center">
                  <Target className="w-7 h-7 text-[#F59E0B]" />
                </div>
                <h3 className="heading-font text-2xl font-bold text-[#0B2545] uppercase tracking-tight">
                  Our Mission
                </h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                To provide world-class logistics solutions that empower businesses to thrive in the 
                global marketplace. We are committed to delivering every shipment with precision, 
                transparency, and care, while continuously innovating to exceed our clients' expectations.
              </p>
            </motion.div>

            <motion.div 
              variants={fadeInUp}
              className="bg-white p-10 border border-slate-200"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#0B2545] flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-[#F59E0B]" />
                </div>
                <h3 className="heading-font text-2xl font-bold text-[#0B2545] uppercase tracking-tight">
                  Our Vision
                </h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                To be the most trusted and innovative logistics partner for businesses worldwide. 
                We envision a future where seamless global trade connects communities, fosters growth, 
                and creates opportunities for all—and we're committed to making that vision a reality.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Core Values Section
const CoreValuesSection = () => {
  const values = [
    {
      icon: Shield,
      title: "Integrity",
      description: "We conduct our business with honesty and transparency, building trust through every interaction."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We strive for the highest standards in everything we do, continuously improving our services."
    },
    {
      icon: Users,
      title: "Partnership",
      description: "We view our clients as partners, working collaboratively to achieve shared success."
    },
    {
      icon: Heart,
      title: "Care",
      description: "We handle every shipment with the utmost care, treating your cargo as our own."
    },
    {
      icon: Globe,
      title: "Global Mindset",
      description: "We embrace diversity and think globally while acting locally to serve our clients better."
    },
    {
      icon: Clock,
      title: "Reliability",
      description: "We deliver on our promises, consistently meeting deadlines and exceeding expectations."
    }
  ];

  return (
    <section className="py-24 bg-white" data-testid="core-values-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4">What We Stand For</p>
            <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight mb-4">
              Our Core Values
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              These principles guide every decision we make and every shipment we handle
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="p-8 border border-slate-200 hover:border-[#F59E0B] transition-colors"
                  data-testid={`core-value-${index}`}
                >
                  <div className="w-12 h-12 bg-slate-100 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#0B2545]" />
                  </div>
                  <h3 className="heading-font text-xl font-bold text-[#0B2545] uppercase tracking-tight mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const reasons = [
    "Strategic location in Amsterdam, Europe's logistics hub",
    "Comprehensive sea, air, and land freight solutions",
    "24/7 customer support and real-time tracking",
    "Experienced team with deep industry expertise",
    "Competitive pricing with transparent quotes",
    "Full insurance coverage for peace of mind",
    "Customs clearance and documentation support",
    "Environmentally conscious shipping options"
  ];

  return (
    <section className="py-24 bg-slate-50" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp}>
            <img 
              src="https://images.unsplash.com/photo-1768796373360-95d80c5830fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBkaXN0cmlidXRpb24lMjBjZW50ZXIlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzM1MzgzOTN8MA&ixlib=rb-4.1.0&q=85"
              alt="Warehouse Operations"
              className="w-full h-[450px] object-cover"
            />
          </motion.div>

          <motion.div variants={fadeInUp}>
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4">Why Sky Cargo</p>
            <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight mb-6">
              The Sky Cargo Advantage
            </h2>
            
            <p className="text-slate-600 leading-relaxed mb-8">
              Choosing the right logistics partner can make all the difference for your business. 
              Here's why companies across the globe trust Sky Cargo Delivery with their shipping needs.
            </p>

            <div className="grid grid-cols-1 gap-3">
              {reasons.map((reason, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0" />
                  <span className="text-slate-700">{reason}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Company Info Section
const CompanyInfoSection = () => {
  return (
    <section className="py-24 bg-[#0B2545]" data-testid="company-info-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4">Our Headquarters</p>
            <h2 className="heading-font text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight">
              Located in the Heart of Europe
            </h2>
          </motion.div>

          <motion.div 
            variants={fadeInUp}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-[#F59E0B] flex items-center justify-center mx-auto mb-6">
                <Building className="w-8 h-8 text-[#0B2545]" />
              </div>
              <h3 className="heading-font text-xl font-bold text-white uppercase tracking-tight mb-3">
                Head Office
              </h3>
              <p className="text-slate-300 text-sm">
                VISSERINGWEG 11<br />
                1112 AS AMSTERDAM<br />
                THE NETHERLANDS
              </p>
            </div>

            <div className="text-center p-8">
              <div className="w-16 h-16 bg-[#F59E0B] flex items-center justify-center mx-auto mb-6">
                <Briefcase className="w-8 h-8 text-[#0B2545]" />
              </div>
              <h3 className="heading-font text-xl font-bold text-white uppercase tracking-tight mb-3">
                Business Hours
              </h3>
              <p className="text-slate-300 text-sm">
                Monday - Friday: 8:00 - 18:00<br />
                Saturday: 9:00 - 14:00<br />
                24/7 Emergency Support
              </p>
            </div>

            <div className="text-center p-8">
              <div className="w-16 h-16 bg-[#F59E0B] flex items-center justify-center mx-auto mb-6">
                <Globe className="w-8 h-8 text-[#0B2545]" />
              </div>
              <h3 className="heading-font text-xl font-bold text-white uppercase tracking-tight mb-3">
                Domain
              </h3>
              <p className="text-slate-300 text-sm">
                skycargodelivery.com<br />
                Serving clients worldwide<br />
                Since establishment
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-20 bg-white" data-testid="about-cta">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.h2 
            variants={fadeInUp}
            className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight mb-4"
          >
            Ready to Partner with Us?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-slate-600 mb-8 max-w-xl mx-auto"
          >
            Let's discuss how Sky Cargo Delivery can support your logistics needs. Our team is ready to help.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/contact">
              <Button 
                className="btn-primary h-12 px-8 rounded-sm"
                data-testid="about-cta-btn"
              >
                Contact Our Team <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      <PageHeader />
      <CompanyStorySection />
      <MissionVisionSection />
      <CoreValuesSection />
      <WhyChooseUsSection />
      <CompanyInfoSection />
      <CTASection />
    </div>
  );
};

export default AboutPage;
